import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../Constants';
import { AuthContext } from "../context/AuthContext";
import {
  FiKey, FiSave, FiRefreshCw, FiEye, FiEyeOff, FiCopy, FiGlobe, FiTag, FiPlus, FiTrash2, FiTrash,
  FiSliders, FiPlay, FiToggleLeft, FiToggleRight, FiInbox, FiCode, FiCheckCircle, FiAlertCircle,
  FiChevronDown, FiX, FiArrowRight, FiDatabase, FiShield, FiBookOpen, FiArrowLeft
} from 'react-icons/fi';
import { toast } from 'react-toastify';

// Mirrors LeadFieldMapping.CUSTOM_FIELD_PREFIX on the backend.
const CUSTOM_FIELD_PREFIX = 'custom.';
const INGESTION_POINT = 'WEBSITE_FORM';
const DEFAULT_WEBSITE_MAPPING = [
  { sourceField: 'name', targetField: 'name', defaultValue: '' },
  { sourceField: 'email', targetField: 'email', defaultValue: '' },
  { sourceField: 'phone', targetField: 'phone', defaultValue: '' },
  { sourceField: 'address', targetField: 'address', defaultValue: '' },
  { sourceField: 'comment', targetField: 'comment', defaultValue: '' },
  { sourceField: 'projectName', targetField: 'projectName', defaultValue: '' },
];
const RESULT_FIELDS = [
  'name', 'email', 'phone', 'otherContactNo', 'companyName', 'reference',
  'address', 'comment', 'city', 'state', 'projectName',
];

const emptyRow = () => ({ sourceField: '', targetField: '', defaultValue: '' });

const KeyIntegration = () => {
  const { user } = useContext(AuthContext);
  
  // Tab State & Docs State with Session Storage for Persistence on Refresh
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('keyIntegrationActiveTab') || 'api-key';
    }
    return 'api-key';
  });

  const [showDocs, setShowDocs] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('keyIntegrationShowDocs') === 'true';
    }
    return false;
  });

  // Save states to session storage whenever they change
  useEffect(() => {
    sessionStorage.setItem('keyIntegrationActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem('keyIntegrationShowDocs', showDocs);
  }, [showDocs]);

  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [allowedOrigins, setAllowedOrigins] = useState([]);
  const [hasExistingOriginsData, setHasExistingOriginsData] = useState(false);
  const [isOriginsLoading, setIsOriginsLoading] = useState(false);
  const [originsError, setOriginsError] = useState(null);
  const [newOriginUrl, setNewOriginUrl] = useState('');
  const [newOriginLabel, setNewOriginLabel] = useState('');
  const [newOriginEndpointKey, setNewOriginEndpointKey] = useState('');

  // --- Lead Field Mapping state (WEBSITE_FORM ingestion point) ---
  const [endpointKey, setEndpointKey] = useState('default');
  const [availableEndpointKeys, setAvailableEndpointKeys] = useState(['default']);
  const [newEndpointKeyInput, setNewEndpointKeyInput] = useState('');
  const [showAddEndpoint, setShowAddEndpoint] = useState(false);

  const [mappingConfig, setMappingConfig] = useState(null); // saved config, or null if none (default in use)
  const [mappingRows, setMappingRows] = useState(DEFAULT_WEBSITE_MAPPING);
  const [usingDefaultMapping, setUsingDefaultMapping] = useState(true);
  const [targetFields, setTargetFields] = useState([]);
  const [discoveredFields, setDiscoveredFields] = useState([]);
  const [samples, setSamples] = useState([]);
  const [showSamples, setShowSamples] = useState(false);

  const [isMappingLoading, setIsMappingLoading] = useState(false);
  const [isMappingSaving, setIsMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState(null);

  const [isTesting, setIsTesting] = useState(false);
  const [testPayloadText, setTestPayloadText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  const isCompanyOwner = user?.accountType === "COMPANY";
  const companyId = isCompanyOwner ? user?.id : user?.companyId;
  const token = localStorage.getItem('token');

  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  // Fetch API Key
  const fetchApiKey = async () => {
    try {
      const { data } = await api.get(`/api/apiKey/${companyId}`);
      if (data) {
        setApiKey(data.apiKey);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to fetch API key');
      }
    }
  };

  // Generate API Key
  const generateApiKey = async () => {
    setIsLoading(true);
    try {
      await api.post(`/api/apiKey/create/${companyId}`);
      await fetchApiKey();
      toast.success('API key generated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate API key');
    } finally {
      setIsLoading(false);
    }
  };

  // Update API Key
  const updateApiKey = async () => {
    setIsLoading(true);
    try {
      await api.put(`/api/apiKey/update/${companyId}`);
      await fetchApiKey();
      toast.success('API key updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update API key');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, type = "Code") => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  // --- B. Get All Whitelisted Domains ---
  const fetchAllowedOrigins = async () => {
    try {
      const { data } = await api.get(`/api/crossOrigin/${companyId}/origins`);
      if (data && data.length > 0) {
        setAllowedOrigins(data);
        setHasExistingOriginsData(true);
      } else {
        setAllowedOrigins([]);
        setHasExistingOriginsData(false);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setOriginsError('Failed to fetch whitelisted domains');
      } else {
        setAllowedOrigins([]);
        setHasExistingOriginsData(false);
      }
    }
  };

  const normalizeUrl = (url) => url.trim().replace(/\/+$/, '');

  // --- A. Create Initial Whitelist Record  &  C. Add/Update Whitelisted Domains ---
  const addAllowedOrigin = async () => {
    const url = normalizeUrl(newOriginUrl);
    const label = newOriginLabel.trim();
    const ek = newOriginEndpointKey.trim();

    if (!/^https?:\/\/.+/.test(url)) {
      toast.error('Enter a full URL including http:// or https://, without a trailing slash');
      return;
    }
    if (!label) {
      toast.error('Enter a label for this domain, e.g. "Production Website"');
      return;
    }
    if (allowedOrigins.some((origin) => origin.url === url)) {
      toast.error('This domain is already whitelisted');
      return;
    }

    setIsOriginsLoading(true);
    setOriginsError(null);
    try {
      const payload = [ek ? { url, projectName: label, endpointKey: ek } : { url, projectName: label }];

      if (hasExistingOriginsData) {
        await api.post(`/api/crossOrigin/${companyId}/update-origins`, payload);
      } else {
        try {
          await api.post(`/api/crossOrigin/create/${companyId}`, payload);
        } catch (createErr) {
          const msg = createErr.response?.data?.message || createErr.response?.data || '';
          if (createErr.response?.status === 400 && String(msg).toLowerCase().includes('already exists')) {
            await api.post(`/api/crossOrigin/${companyId}/update-origins`, payload);
          } else {
            throw createErr;
          }
        }
      }

      toast.success('Domain whitelisted successfully');
      setNewOriginUrl('');
      setNewOriginLabel('');
      setNewOriginEndpointKey('');
      await fetchAllowedOrigins();
    } catch (err) {
      const status = err.response?.status;
      let message = err.response?.data?.message || err.response?.data || 'Failed to whitelist domain';
      if (status === 403) {
        message = 'Only company accounts can manage allowed origins.';
      }
      setOriginsError(message);
      toast.error(message);
    } finally {
      setIsOriginsLoading(false);
    }
  };

  // --- D. Delete a Single Domain Entry ---
  const deleteAllowedOrigin = async (origin) => {
    if (!window.confirm(`Remove ${origin.url} from your whitelist? Requests from this domain will stop working immediately.`)) {
      return;
    }
    setIsOriginsLoading(true);
    setOriginsError(null);
    try {
      await api.delete(`/api/crossOrigin/${companyId}/deleteSingleEntry`, {
        data: { url: origin.url, projectName: origin.projectName }
      });
      toast.success('Domain removed');
      await fetchAllowedOrigins();
    } catch (err) {
      const status = err.response?.status;
      const message = status === 404
        ? 'Origin entry not found.'
        : (err.response?.data?.message || 'Failed to remove domain');
      setOriginsError(message);
      toast.error(message);
    } finally {
      setIsOriginsLoading(false);
    }
  };

  // --- E. Delete All Whitelisted Domains ---
  const deleteAllAllowedOrigins = async () => {
    if (allowedOrigins.length === 0) return;
    if (!window.confirm('Remove ALL whitelisted domains for your account? Every browser-based integration will stop working immediately until you add domains back.')) {
      return;
    }
    setIsOriginsLoading(true);
    setOriginsError(null);
    try {
      await api.delete(`/api/crossOrigin/${companyId}`);
      toast.success('All whitelisted domains removed');
      setAllowedOrigins([]);
      setHasExistingOriginsData(false);
    } catch (err) {
      const status = err.response?.status;
      const message = status === 404
        ? 'Record not found for companyId.'
        : (err.response?.data?.message || 'Failed to clear whitelist');
      setOriginsError(message);
      toast.error(message);
    } finally {
      setIsOriginsLoading(false);
    }
  };

  // ================= Lead Field Mapping =================
  const fetchTargetFields = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/lead-mapping/targets/${companyId}`);
      setTargetFields(data || []);
    } catch (err) {
      console.error('Failed to fetch target fields', err);
    }
  }, [companyId]);

  const fetchConfiguredEndpointKeys = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/lead-mapping/company/${companyId}/${INGESTION_POINT}`);
      const keys = (data || []).map((c) => c.endpointKey).filter(Boolean);
      setAvailableEndpointKeys((prev) => Array.from(new Set(['default', ...prev, ...keys])));
    } catch (err) {
      console.error('Failed to fetch configured endpoints', err);
    }
  }, [companyId]);

  const fetchDiscoveredFields = useCallback(async (ek) => {
    try {
      const { data } = await api.get(
        `/api/lead-mapping/samples/${companyId}/${INGESTION_POINT}/${encodeURIComponent(ek)}/fields`
      );
      setDiscoveredFields(data || []);
    } catch (err) {
      setDiscoveredFields([]);
    }
  }, [companyId]);

  const fetchSamples = useCallback(async (ek) => {
    try {
      const { data } = await api.get(
        `/api/lead-mapping/samples/${companyId}/${INGESTION_POINT}/${encodeURIComponent(ek)}`,
        { params: { limit: 10 } }
      );
      setSamples(data || []);
    } catch (err) {
      setSamples([]);
    }
  }, [companyId]);

  const fetchMappingConfig = useCallback(async (ek) => {
    setIsMappingLoading(true);
    setMappingError(null);
    try {
      const { data } = await api.get(
        `/api/lead-mapping/company/${companyId}/${INGESTION_POINT}/${encodeURIComponent(ek)}`
      );
      setMappingConfig(data);
      setMappingRows(
        data.mappings && data.mappings.length > 0
          ? data.mappings.map((m) => ({ sourceField: m.sourceField || '', targetField: m.targetField || '', defaultValue: m.defaultValue || '' }))
          : DEFAULT_WEBSITE_MAPPING
      );
      setUsingDefaultMapping(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setMappingConfig(null);
        setMappingRows(DEFAULT_WEBSITE_MAPPING);
        setUsingDefaultMapping(true);
      } else {
        setMappingError('Failed to load mapping configuration');
      }
    } finally {
      setIsMappingLoading(false);
    }
  }, [companyId]);

  const loadEndpointContext = useCallback((ek) => {
    fetchMappingConfig(ek);
    fetchDiscoveredFields(ek);
    fetchSamples(ek);
    setTestResult(null);
    setTestError(null);
  }, [fetchMappingConfig, fetchDiscoveredFields, fetchSamples]);

  useEffect(() => {
    if (companyId) {
      fetchApiKey();
      fetchAllowedOrigins();
      fetchTargetFields();
      fetchConfiguredEndpointKeys();
      loadEndpointContext(endpointKey);
    }
  }, [companyId]);

  useEffect(() => {
    const originKeys = allowedOrigins.map((o) => o.endpointKey).filter(Boolean);
    if (originKeys.length > 0) {
      setAvailableEndpointKeys((prev) => Array.from(new Set(['default', ...prev, ...originKeys])));
    }
  }, [allowedOrigins]);

  const handleEndpointKeyChange = (ek) => {
    setEndpointKey(ek);
    loadEndpointContext(ek);
  };

  const addNewEndpointKey = () => {
    const ek = newEndpointKeyInput.trim();
    if (!ek) {
      toast.error('Enter an endpoint key, e.g. "landingPageB"');
      return;
    }
    setAvailableEndpointKeys((prev) => Array.from(new Set([...prev, ek])));
    setNewEndpointKeyInput('');
    setShowAddEndpoint(false);
    handleEndpointKeyChange(ek);
  };

  const updateMappingRow = (index, field, value) => {
    setMappingRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addMappingRow = () => setMappingRows((rows) => [...rows, emptyRow()]);
  const removeMappingRow = (index) => setMappingRows((rows) => rows.filter((_, i) => i !== index));
  const resetToDefaultMapping = () => setMappingRows(DEFAULT_WEBSITE_MAPPING);
  const validMappingRows = () => mappingRows.filter((r) => r.sourceField.trim() && r.targetField.trim());

  const saveMapping = async () => {
    const rows = validMappingRows();
    if (rows.length === 0) {
      toast.error('Add at least one mapping (a source field and a target field)');
      return;
    }
    setIsMappingSaving(true);
    setMappingError(null);
    try {
      const payload = {
        companyId,
        ingestionPoint: INGESTION_POINT,
        endpointKey,
        mappings: rows.map((r) => ({
          sourceField: r.sourceField.trim(),
          targetField: r.targetField.trim(),
          defaultValue: r.defaultValue?.trim() ? r.defaultValue.trim() : null,
        })),
      };
      const { data } = await api.post('/api/lead-mapping', payload);
      setMappingConfig(data);
      setUsingDefaultMapping(false);
      toast.success('Mapping saved');
      fetchConfiguredEndpointKeys();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save mapping';
      setMappingError(message);
      toast.error(message);
    } finally {
      setIsMappingSaving(false);
    }
  };

  const toggleMappingActive = async () => {
    if (!mappingConfig) return;
    const nextActive = !mappingConfig.active;
    try {
      await api.put(
        `/api/lead-mapping/company/${companyId}/${INGESTION_POINT}/${encodeURIComponent(endpointKey)}/active`,
        null,
        { params: { active: nextActive } }
      );
      setMappingConfig((cfg) => ({ ...cfg, active: nextActive }));
      toast.success(nextActive ? 'Mapping enabled' : 'Mapping disabled - falling back to the default mapping');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update mapping status');
    }
  };

  const deleteMapping = async () => {
    if (!mappingConfig) return;
    if (!window.confirm(`Delete the saved mapping for endpoint "${endpointKey}"? Leads from it will fall back to the default mapping.`)) {
      return;
    }
    try {
      await api.delete(`/api/lead-mapping/${mappingConfig.id}`, { params: { companyId } });
      toast.success('Mapping configuration deleted');
      setMappingConfig(null);
      setMappingRows(DEFAULT_WEBSITE_MAPPING);
      setUsingDefaultMapping(true);
      fetchConfiguredEndpointKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete mapping');
    }
  };

  const testMapping = async () => {
    const rows = validMappingRows();
    if (rows.length === 0) {
      toast.error('Add at least one mapping before testing');
      return;
    }

    let samplePayload;
    if (testPayloadText.trim()) {
      try {
        samplePayload = JSON.parse(testPayloadText);
      } catch (e) {
        toast.error('Custom test payload must be valid JSON');
        return;
      }
    }

    setIsTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const payload = {
        companyId,
        ingestionPoint: INGESTION_POINT,
        endpointKey,
        mappings: rows.map((r) => ({
          sourceField: r.sourceField.trim(),
          targetField: r.targetField.trim(),
          defaultValue: r.defaultValue?.trim() ? r.defaultValue.trim() : null,
        })),
        ...(samplePayload ? { samplePayload } : {}),
      };
      const { data } = await api.post('/api/lead-mapping/test', payload);
      setTestResult(data);
    } catch (err) {
      setTestError(err.response?.data?.message || 'Failed to test mapping');
    } finally {
      setIsTesting(false);
    }
  };

  const handleUseSampleAsTestPayload = (payload) => {
    setTestPayloadText(JSON.stringify(payload, null, 2));
    setShowSamples(false);
  };

  const displayApiKey = apiKey || "your_api_key_here";
  const apiEndpoint = "https://apilms.deetyasoft.com/api/publicLeads";

  // Reusable unified input class for strict purple theme on hover/focus - slightly smaller font (text-[13px])
  const inputClass = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white transition-all duration-200 outline-none hover:border-[#673AB7] focus:border-[#673AB7] focus:ring-1 focus:ring-[#673AB7] text-[13px]";

  // =====================================================================
  // FULL PAGE DOCUMENTATION VIEW
  // =====================================================================
  if (showDocs) {
    return (
      <div className="p-4 md:p-6 bg-[#FAF8FF] shadow-inner dark:bg-gray-900 dark:text-white min-h-screen cfg-font">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
        <div className="max-w-4xl mx-auto flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#673AB7]/10 rounded-xl">
              <FiBookOpen className="text-xl text-[#673AB7]" />
            </div>
            <div>
              <h1 className="text-xl font-bold cfg-heading text-gray-800 dark:text-white">API Documentation</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Custom Website Lead Integration</p>
            </div>
          </div>
          <button
            onClick={() => setShowDocs(false)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors text-[13px] shadow-sm"
          >
            <FiArrowLeft size={16} /> Back to Integration
          </button>
        </div>

        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-8 animate-fade-in">
          <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
            This API allows you to automatically send leads generated from your external websites, landing pages, or custom forms directly into your CRM account.
          </p>

          {/* 1. Endpoint Details */}
          <section className="mb-8">
            <h3 className="text-base font-bold cfg-heading mb-3 text-gray-800 dark:text-white flex items-center gap-2">
              <span className="bg-[#673AB7] text-white w-5 h-5 rounded flex items-center justify-center text-xs">1</span> 
              Endpoint Details
            </h3>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-[13px] font-mono break-all mb-3">
                <span className="px-2.5 py-0.5 bg-[#F3E8FF] text-[#673AB7] dark:bg-blue-900/40 dark:text-blue-300 font-bold rounded">POST</span>
                <span className="text-gray-800 dark:text-gray-200 font-semibold">{apiEndpoint}</span>
              </div>
              <ul className="text-[13px] text-gray-600 dark:text-gray-400 space-y-1.5 mt-3 ml-1 list-disc list-inside">
                <li><strong className="text-gray-800 dark:text-gray-200">Content-Type:</strong> application/json</li>
                <li><strong className="text-gray-800 dark:text-gray-200">Query param (optional):</strong> <code>?endpointKey=&lt;key&gt;</code> - routes this submission to a specific mapping configured above, instead of "default" or whatever your whitelisted domain resolves to.</li>
              </ul>
            </div>
          </section>

          {/* 2. Authentication (Headers) */}
          <section className="mb-8">
            <h3 className="text-base font-bold cfg-heading mb-3 text-gray-800 dark:text-white flex items-center gap-2">
              <span className="bg-[#673AB7] text-white w-5 h-5 rounded flex items-center justify-center text-xs">2</span> 
              Authentication (Headers)
            </h3>
            <p className="text-[13px] text-gray-600 dark:text-gray-400 mb-3">
              To authenticate your request, you must include your unique API key in the request headers.
            </p>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse text-[13px]">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="p-3 font-semibold border-b dark:border-gray-700 w-1/3">Header</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700 w-24">Required</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800">
                  <tr>
                    <td className="p-3 font-mono font-medium">X-API-KEY</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[11px] font-bold">Yes</span></td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">Your company's unique API Key.</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-mono font-medium">Content-Type</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[11px] font-bold">Yes</span></td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">Must be set to <code>application/json</code>.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 3. Request Payload (JSON Body) */}
          <section className="mb-8">
            <h3 className="text-base font-bold cfg-heading mb-3 text-gray-800 dark:text-white flex items-center gap-2">
              <span className="bg-[#673AB7] text-white w-5 h-5 rounded flex items-center justify-center text-xs">3</span> 
              Request Payload (JSON Body)
            </h3>
            <p className="text-[13px] text-gray-600 dark:text-gray-400 mb-3">
              Send the lead details in the body of your request as a JSON object. By default the fields below are read as-is - use the <strong>Lead Field Mapping</strong> section above if your form sends different key names, nested objects, or extra fields you want captured as custom fields.
            </p>
            
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm mb-4">
              <table className="w-full text-left border-collapse text-[13px]">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Field</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Type</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Required</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800">
                  {[
                    { field: 'name', type: 'String', req: 'Required*', desc: 'The full name of the lead.' },
                    { field: 'email', type: 'String', req: 'Optional', desc: 'The email address of the lead.' },
                    { field: 'phone', type: 'String', req: 'Required*', desc: 'The contact number of the lead. Used to detect repeat submissions.' },
                    { field: 'comment', type: 'String', req: 'Optional', desc: 'Any message, inquiry, or additional details submitted.' },
                    { field: 'address', type: 'String', req: 'Optional', desc: 'The physical address or location of the lead.' },
                    { field: 'projectName', type: 'String', req: 'Optional', desc: 'The exact name of the Project/Campaign in your CRM.' },
                  ].map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-3 font-mono font-medium">{item.field}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-400">{item.type}</td>
                      <td className="p-3 text-gray-500 text-[11px] uppercase tracking-wider">{item.req}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-400">{item.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-[#F3E8FF] dark:bg-blue-900/20 border-l-4 border-[#673AB7] p-4 rounded-r-lg text-[13px] text-gray-700 dark:text-gray-300 space-y-2">
              <p><strong>Important Note on projectName:</strong> If you provide a projectName that exactly matches an active project in your CRM, the system will automatically distribute the lead to your executives based on your allocation settings. If the project is left blank or does not exist (and no whitelisted domain default applies), the lead will still be saved successfully but will remain "Unassigned" in your CRM pool.</p>
              <p><strong>*Required after mapping:</strong> name and phone are required on the resulting lead, but they don't have to be called "name"/"phone" in your raw payload - map whichever keys you actually send to those targets above.</p>
            </div>
          </section>

          {/* 4. Integration Examples */}
          <section className="mb-8">
            <h3 className="text-base font-bold cfg-heading mb-4 text-gray-800 dark:text-white flex items-center gap-2">
              <span className="bg-[#673AB7] text-white w-5 h-5 rounded flex items-center justify-center text-xs">4</span> 
              Integration Examples
            </h3>
            
            {/* cURL Block */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-800 mb-5">
              <div className="bg-gray-900 text-gray-400 px-4 py-2.5 flex justify-between items-center text-[11px] uppercase tracking-wider font-mono border-b border-gray-700">
                <span>cURL</span>
                <button 
                  onClick={() => copyToClipboard(`curl -X POST ${apiEndpoint} \\\n     -H "Content-Type: application/json" \\\n     -H "X-API-KEY: ${displayApiKey}" \\\n     -d '{\n           "name": "John Doe",\n           "email": "johndoe@example.com",\n           "phone": "9876543210",\n           "comment": "I would like to know more about your premium services.",\n           "projectName": "Summer Marketing Campaign"\n         }'`)} 
                  className="hover:text-white flex items-center gap-1.5 transition"
                >
                  <FiCopy size={12}/> Copy Code
                </button>
              </div>
              <pre className="bg-[#1e1e2e] text-green-400 p-4 overflow-x-auto text-[13px] font-mono leading-relaxed">
{`curl -X POST ${apiEndpoint} \\
     -H "Content-Type: application/json" \\
     -H "X-API-KEY: ${displayApiKey}" \\
     -d '{
           "name": "John Doe",
           "email": "johndoe@example.com",
           "phone": "9876543210",
           "comment": "I would like to know more about your premium services.",
           "projectName": "Summer Marketing Campaign"
         }'`}
              </pre>
            </div>

            {/* JS Fetch Block */}
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-800 mb-5">
              <div className="bg-gray-900 text-gray-400 px-4 py-2.5 flex justify-between items-center text-[11px] uppercase tracking-wider font-mono border-b border-gray-700">
                <span>javascript - fetch API</span>
                <button 
                  onClick={() => copyToClipboard(`const leadData = {\n    name: "John Doe",\n    email: "johndoe@example.com",\n    phone: "9876543210",\n    comment: "I would like to know more about your premium services.",\n    projectName: "Summer Marketing Campaign"\n};\n\nfetch("${apiEndpoint}", {\n    method: "POST",\n    headers: {\n        "Content-Type": "application/json",\n        "X-API-KEY": "${displayApiKey}"\n    },\n    body: JSON.stringify(leadData)\n})\n.then(response => response.text())\n.then(data => console.log("Success:", data))\n.catch(error => console.error("Error:", error));`)} 
                  className="hover:text-white flex items-center gap-1.5 transition"
                >
                  <FiCopy size={12}/> Copy Code
                </button>
              </div>
              <pre className="bg-[#1e1e2e] text-[#C4B5FD] p-4 overflow-x-auto text-[13px] font-mono leading-relaxed">
{`const leadData = {
    name: "John Doe",
    email: "johndoe@example.com",
    phone: "9876543210",
    comment: "I would like to know more about your premium services.",
    projectName: "Summer Marketing Campaign"
};

fetch("${apiEndpoint}", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        "X-API-KEY": "${displayApiKey}"
    },
    body: JSON.stringify(leadData)
})
.then(response => response.text())
.then(data => console.log("Success:", data))
.catch(error => console.error("Error:", error));`}
              </pre>
            </div>
          </section>

          {/* 5. Standard Responses */}
          <section>
            <h3 className="text-base font-bold cfg-heading mb-3 text-gray-800 dark:text-white flex items-center gap-2">
              <span className="bg-[#673AB7] text-white w-5 h-5 rounded flex items-center justify-center text-xs">5</span> 
              Standard Responses
            </h3>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse text-[13px]">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="p-3 font-semibold border-b dark:border-gray-700 w-36">Status Code</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Response Text</th>
                    <th className="p-3 font-semibold border-b dark:border-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800">
                  <tr>
                    <td className="p-3 text-green-600 dark:text-green-500 font-bold">200 OK</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300 font-mono text-[11px]">"Lead submitted successfully."</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">The lead was successfully captured and saved in the CRM.</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-orange-500 font-bold">401 Unauthorized</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300 font-mono text-[11px]">"Invalid API key." / "Phone is required"</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">The X-API-KEY is missing/incorrect, account is inactive, or the payload didn't resolve a name/phone.</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-red-500 font-bold">500 Server Error</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300 font-mono text-[11px]">"Error: [Details]"</td>
                    <td className="p-3 text-gray-600 dark:text-gray-400">Something went wrong on the server side.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          
        </div>
      </div>
    );
  }

  // =====================================================================
  // MAIN INTEGRATION VIEW (Tabs)
  // =====================================================================
  return (
    <div className="p-4 md:p-6 bg-[#FAF8FF] shadow-inner dark:bg-gray-900 dark:text-white min-h-screen cfg-font">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#673AB7]/10 rounded-xl">
            <FiKey className="text-xl text-[#673AB7]" />
          </div>
          <div>
            <h1 className="text-xl font-bold cfg-heading text-gray-800 dark:text-white">API Integration</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Manage API keys, CORS policies, and routing logic.</p>
          </div>
        </div>
        {/* NEW DOCUMENTATION BUTTON */}
        <button
          onClick={() => setShowDocs(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#F3E8FF] text-[#673AB7] hover:bg-[#E9D5FF] dark:bg-[#673AB7]/10 dark:hover:bg-[#673AB7]/20 border border-[#E9D5FF] dark:border-transparent font-semibold rounded-lg transition-colors text-[13px] shadow-sm"
        >
          <FiBookOpen size={16} /> API Documentation
        </button>
      </div>

      {!isCompanyOwner && (
        <div className="mb-5 p-3 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 text-xs flex items-start gap-2 shadow-sm">
          <FiAlertCircle className="mt-0.5 text-base" />
          <p>Only company accounts can manage the API key and allowed origins. You can view this page, but changes may be rejected by the server.</p>
        </div>
      )}

      {error && (
        <div className="mb-5 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-xs flex items-start gap-2 shadow-sm">
          <FiAlertCircle className="mt-0.5 text-base" />
          <p>{error}</p>
        </div>
      )}

      {/* Tabs Navigation (With Step Indicators added) */}
      <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 mb-6 scrollbar-hide">
        <button
          onClick={() => setActiveTab('api-key')}
          className={`px-5 py-3 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'api-key' ? 'border-[#673AB7] text-[#673AB7]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${activeTab === 'api-key' ? 'bg-[#673AB7]/10' : 'bg-gray-100 dark:bg-gray-800'}`}>STEP 1</span>
          <FiKey size={14} /> API Key
        </button>
        <button
          onClick={() => setActiveTab('cors')}
          className={`px-5 py-3 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'cors' ? 'border-[#673AB7] text-[#673AB7]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${activeTab === 'cors' ? 'bg-[#673AB7]/10' : 'bg-gray-100 dark:bg-gray-800'}`}>STEP 2</span>
          <FiShield size={14} /> Domain Security (CORS)
        </button>
        <button
          onClick={() => setActiveTab('mapping')}
          className={`px-5 py-3 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'mapping' ? 'border-[#673AB7] text-[#673AB7]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${activeTab === 'mapping' ? 'bg-[#673AB7]/10' : 'bg-gray-100 dark:bg-gray-800'}`}>STEP 3</span>
          <FiSliders size={14} /> Lead Mapping
        </button>
      </div>

      {/* =====================================================================
          TAB 1: API KEY MANAGEMENT
          ===================================================================== */}
      {activeTab === 'api-key' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 animate-fade-in">
          <div className="max-w-3xl">
            <h2 className="text-lg font-bold cfg-heading text-gray-800 dark:text-white mb-2">Step 1: Authentication Key</h2>
            <p className="text-gray-500 dark:text-gray-400 text-[13px] mb-6">
              This API key is required to authenticate your form submissions. Keep it secure and never expose it in public client-side code where possible.
            </p>

            <div className="flex flex-col sm:flex-row items-end gap-3">
              <div className="flex-1 w-full relative">
                <label className="block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Your Secret API Key</label>
                <input
                  type={showApiKey ? "text" : "password"}
                  value={apiKey}
                  readOnly
                  placeholder="Click generate to create your API key"
                  className={`${inputClass} pr-20`}
                />
                {apiKey && (
                  <div className="absolute right-2 top-[30px] flex items-center gap-1">
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="p-1 text-gray-400 hover:text-[#673AB7] dark:hover:text-[#673AB7] transition-colors rounded-md"
                      title={showApiKey ? "Hide Key" : "Show Key"}
                    >
                      {showApiKey ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                    <div className="w-px h-3 bg-gray-300 dark:bg-gray-600 mx-0.5"></div>
                    <button
                      onClick={() => copyToClipboard(apiKey, 'API key')}
                      className="p-1 text-gray-400 hover:text-[#673AB7] dark:hover:text-[#673AB7] transition-colors rounded-md"
                      title="Copy API key"
                    >
                      <FiCopy size={16} />
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={apiKey ? updateApiKey : generateApiKey}
                disabled={isLoading}
                className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-[#673AB7] to-[#8B5CF6] hover:opacity-90 text-white text-[13px] font-medium rounded-lg transition disabled:opacity-70 flex items-center justify-center gap-2 shadow-sm h-[38px]"
              >
                {isLoading ? (
                  <FiRefreshCw className="animate-spin text-sm" />
                ) : apiKey ? (
                  <><FiRefreshCw size={14} /> Regenerate Key</>
                ) : (
                  <><FiKey size={14} /> Generate Key</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 2: CORS WHITELISTING
          ===================================================================== */}
      {activeTab === 'cors' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 animate-fade-in">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3">
            <div>
              <h2 className="text-lg font-bold cfg-heading text-gray-800 dark:text-white mb-1.5">Step 2: Whitelisted Domains (CORS)</h2>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-2xl">
                Browsers block requests to <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{apiEndpoint}</span> unless the domain is listed here. Add domains that call this API directly via browser JS.
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F3E8FF] text-[#673AB7] dark:bg-purple-900/30 border border-[#E9D5FF] dark:border-purple-800/50">
                {allowedOrigins.length} Active Domains
              </span>
              {allowedOrigins.length > 0 && (
                <button
                  onClick={deleteAllAllowedOrigins}
                  disabled={isOriginsLoading}
                  className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-100 disabled:opacity-60"
                  title="Remove all domains"
                >
                  <FiTrash size={16} />
                </button>
              )}
            </div>
          </div>

          {originsError && (
            <div className="mb-5 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-xs flex items-start gap-2 shadow-sm">
              <FiAlertCircle className="mt-0.5 text-base" />
              <p>{originsError}</p>
            </div>
          )}

          {/* Add Domain Form */}
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-4 rounded-xl mb-6">
            <h3 className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
              <FiPlus className="text-[#673AB7]" /> Add New Domain
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiGlobe className="text-gray-400" size={14} />
                </div>
                <input
                  type="text"
                  placeholder="https://yourwebsite.com"
                  value={newOriginUrl}
                  onChange={(e) => setNewOriginUrl(e.target.value)}
                  className={`${inputClass} pl-8`}
                  disabled={isOriginsLoading}
                />
              </div>
              <div className="md:col-span-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiTag className="text-gray-400" size={14} />
                </div>
                <input
                  type="text"
                  placeholder="Label (e.g. Production)"
                  value={newOriginLabel}
                  onChange={(e) => setNewOriginLabel(e.target.value)}
                  className={`${inputClass} pl-8`}
                  disabled={isOriginsLoading}
                />
              </div>
              <div className="md:col-span-4 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSliders className="text-gray-400" size={14} />
                </div>
                <input
                  type="text"
                  placeholder="Endpoint key (Optional)"
                  value={newOriginEndpointKey}
                  onChange={(e) => setNewOriginEndpointKey(e.target.value)}
                  className={`${inputClass} pl-8`}
                  disabled={isOriginsLoading}
                  title="Leave blank to use default mapping."
                />
              </div>
              <div className="md:col-span-12 flex justify-end mt-1">
                <button
                  onClick={addAllowedOrigin}
                  disabled={isOriginsLoading}
                  className="px-5 py-2 bg-[#673AB7] hover:bg-[#5e35a6] text-white text-[13px] rounded-lg transition disabled:opacity-70 flex items-center justify-center gap-2 font-medium shadow-sm h-[36px]"
                >
                  {isOriginsLoading ? <FiRefreshCw className="animate-spin" size={14} /> : <><FiPlus size={14} /> Whitelist Domain</>}
                </button>
              </div>
            </div>
          </div>

          {/* Domains List */}
          <div>
            <h3 className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-3">Active Configurations</h3>
            {allowedOrigins.length > 0 ? (
              <div className="grid grid-cols-1 gap-2.5">
                {allowedOrigins.map((origin, index) => (
                  <div
                    key={`${origin.url}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-[#673AB7]/30 transition-colors"
                  >
                    <div className="mb-2 sm:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FiGlobe className="text-gray-400" size={14} />
                        <span className="font-mono text-sm font-medium text-gray-800 dark:text-gray-100">{origin.url}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 pl-5">
                        <span className="flex items-center gap-1"><FiTag size={10}/> {origin.projectName}</span>
                        {origin.endpointKey && (
                          <span className="px-2 py-0.5 rounded bg-[#673AB7]/10 text-[#673AB7] dark:bg-[#673AB7]/20 border border-[#673AB7]/20 text-[10px] font-medium">
                            Route: {origin.endpointKey}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteAllowedOrigin(origin)}
                      disabled={isOriginsLoading}
                      className="self-end sm:self-auto p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors border border-transparent hover:border-red-100"
                      title="Remove domain"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                <FiGlobe className="mx-auto text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-[13px] text-gray-500 dark:text-gray-400">No domains whitelisted yet. Requests from browsers on other domains will be blocked.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 3: LEAD FIELD MAPPING
          ===================================================================== */}
      {activeTab === 'mapping' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 animate-fade-in">
          
          {/* Header & Endpoint Selector */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4 border-b border-gray-100 dark:border-gray-700 pb-5">
            <div className="max-w-2xl">
              <h2 className="text-lg font-bold cfg-heading text-gray-800 dark:text-white mb-1.5">Step 3: Lead Field Mapping</h2>
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                Control how data sent from your website maps into CRM lead fields. Create different mapping rules for different endpoints.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-200 dark:border-gray-700 flex-shrink-0">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">Active Endpoint Route</label>
              <div className="flex items-center gap-2">
                <div className="relative w-44">
                  <select
                    value={endpointKey}
                    onChange={(e) => handleEndpointKeyChange(e.target.value)}
                    className={`${inputClass} appearance-none pr-8 py-1.5 cursor-pointer font-medium text-[13px]`}
                  >
                    {availableEndpointKeys.map((ek) => (
                      <option key={ek} value={ek}>{ek}</option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-2.5 top-2.5 text-gray-400 pointer-events-none" size={14} />
                </div>
                
                {!showAddEndpoint ? (
                  <button
                    onClick={() => setShowAddEndpoint(true)}
                    className="p-1.5 text-[#673AB7] hover:bg-[#F3E8FF] dark:hover:bg-[#673AB7]/10 rounded-lg transition-colors border border-transparent hover:border-[#E9D5FF]"
                    title="Add new endpoint route"
                  >
                    <FiPlus size={18} />
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5 animate-fade-in">
                    <input
                      autoFocus
                      type="text"
                      value={newEndpointKeyInput}
                      onChange={(e) => setNewEndpointKeyInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addNewEndpointKey()}
                      placeholder="e.g. landingPageB"
                      className={`${inputClass} w-36 py-1.5 text-[13px]`}
                    />
                    <button onClick={addNewEndpointKey} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg dark:hover:bg-green-900/20 border border-transparent hover:border-green-200">
                      <FiCheckCircle size={18} />
                    </button>
                    <button onClick={() => { setShowAddEndpoint(false); setNewEndpointKeyInput(''); }} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg dark:hover:bg-gray-700 border border-transparent hover:border-gray-300">
                      <FiX size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {mappingError && (
             <div className="mb-5 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-xs flex items-start gap-2 shadow-sm">
               <FiAlertCircle className="mt-0.5 text-base" />
               <p>{mappingError}</p>
             </div>
          )}

          {/* Mapping Controls & Status */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2.5">
              {usingDefaultMapping ? (
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs font-medium border border-gray-200 dark:border-gray-600 flex items-center gap-1.5">
                  <FiAlertCircle size={12}/> Default mapping active
                </span>
              ) : (
                <>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${mappingConfig?.active ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' : 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800'}`}>
                    {mappingConfig?.active ? <><FiCheckCircle size={12}/> Active Route</> : <><FiAlertCircle size={12}/> Disabled (Fallback to default)</>}
                  </span>
                  <button
                    onClick={toggleMappingActive}
                    className="flex items-center gap-1 text-xs font-medium text-[#673AB7] hover:bg-[#F3E8FF] dark:hover:bg-purple-900/20 px-2 py-1 rounded-lg transition"
                  >
                    {mappingConfig?.active ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                    {mappingConfig?.active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={deleteMapping} className="flex items-center gap-1 text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded-lg transition">
                    <FiTrash2 size={14} /> Reset
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => setShowSamples((s) => !s)}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-[#673AB7] hover:text-[#673AB7] transition"
            >
              <FiInbox size={14} /> 
              {showSamples ? 'Hide' : 'View'} Raw Logs ({samples.length})
            </button>
          </div>

          {/* Raw Payload Samples Logs */}
          {showSamples && (
            <div className="mb-6 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 shadow-inner animate-fade-in">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                <FiDatabase className="text-[#673AB7]"/> Recent Payloads Received
              </h4>
              <div className="max-h-60 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-gray-300">
                {samples.length === 0 ? (
                  <div className="text-center py-5 text-xs text-gray-500 italic border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                    No payloads captured yet for this endpoint route.
                  </div>
                ) : (
                  samples.map((s, i) => (
                    <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 bg-white dark:bg-gray-800 shadow-sm relative group">
                      <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-[11px] font-semibold text-gray-500">{s.receivedAt ? new Date(s.receivedAt).toLocaleString() : 'Unknown time'}</span>
                        <button
                          onClick={() => handleUseSampleAsTestPayload(s.payload)}
                          className="text-[11px] font-medium text-[#673AB7] bg-[#F3E8FF] hover:bg-[#E9D5FF] px-2 py-0.5 rounded-full transition flex items-center gap-1"
                        >
                          Use for testing <FiArrowRight size={10} />
                        </button>
                      </div>
                      <pre className="text-[11px] font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">{JSON.stringify(s.payload, null, 2)}</pre>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Mapping Table */}
          {isMappingLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <FiRefreshCw className="animate-spin text-2xl text-[#673AB7]" />
            </div>
          ) : (
            <div className="mb-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-[13px]">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs">
                        <th className="p-3 font-semibold text-gray-700 dark:text-gray-300">Incoming Field <span className="font-normal text-gray-400 text-[11px] ml-1">(JSON Key)</span></th>
                        <th className="p-3 w-10 text-center"></th>
                        <th className="p-3 font-semibold text-gray-700 dark:text-gray-300">Target CRM Field</th>
                        <th className="p-3 font-semibold text-gray-700 dark:text-gray-300">Fallback Value <span className="font-normal text-gray-400 text-[11px] ml-1">(Optional)</span></th>
                        <th className="p-3 w-12 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {mappingRows.map((row, index) => (
                        <tr key={index} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group">
                          <td className="p-2.5">
                            <input
                              list="discovered-source-fields"
                              value={row.sourceField}
                              onChange={(e) => updateMappingRow(index, 'sourceField', e.target.value)}
                              placeholder="e.g. mobile_number"
                              className={`${inputClass} font-mono py-1.5`}
                            />
                          </td>
                          <td className="p-2.5 text-center text-gray-300 dark:text-gray-600">
                            <FiArrowRight size={16} className="mx-auto" />
                          </td>
                          <td className="p-2.5">
                            <select
                              value={row.targetField}
                              onChange={(e) => updateMappingRow(index, 'targetField', e.target.value)}
                              className={`${inputClass} py-1.5`}
                            >
                              <option value="">Select target...</option>
                              <optgroup label="Standard CRM Fields" className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700">
                                {targetFields.filter((t) => t.type === 'STATIC').map((t) => (
                                  <option key={t.key} value={t.key} className="font-normal bg-white dark:bg-gray-800">{t.label}</option>
                                ))}
                              </optgroup>
                              {targetFields.some((t) => t.type === 'CUSTOM') && (
                                <optgroup label="Your Custom Fields" className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 mt-1">
                                  {targetFields.filter((t) => t.type === 'CUSTOM').map((t) => (
                                    <option key={t.key} value={t.key} className="font-normal bg-white dark:bg-gray-800">{t.label}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </td>
                          <td className="p-2.5">
                            <input
                              type="text"
                              value={row.defaultValue}
                              onChange={(e) => updateMappingRow(index, 'defaultValue', e.target.value)}
                              placeholder="If empty, use this..."
                              className={`${inputClass} py-1.5`}
                            />
                          </td>
                          <td className="p-2.5 text-center">
                            <button 
                              onClick={() => removeMappingRow(index)} 
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition opacity-50 group-hover:opacity-100" 
                              title="Delete row"
                            >
                              <FiTrash2 size={16} className="mx-auto" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* DataList for suggestions */}
                <datalist id="discovered-source-fields">
                  {discoveredFields.map((f) => (
                    <option key={f.sourceField} value={f.sourceField}>{String(f.exampleValue ?? '')}</option>
                  ))}
                </datalist>
                
                {/* Table Footer Controls */}
                <div className="bg-gray-50 dark:bg-gray-900 p-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3">
                  <div className="flex gap-2">
                    <button
                      onClick={addMappingRow}
                      className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-[#673AB7] hover:text-[#673AB7] transition shadow-sm flex items-center gap-1.5"
                    >
                      <FiPlus size={14} /> Add Field Map
                    </button>
                    <button
                      onClick={resetToDefaultMapping}
                      className="px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 text-gray-600 dark:text-gray-300 transition shadow-sm flex items-center gap-1.5"
                    >
                      <FiRefreshCw size={12} /> Reset
                    </button>
                  </div>
                  
                  <button
                    onClick={saveMapping}
                    disabled={isMappingSaving}
                    className="w-full sm:w-auto px-6 py-2 bg-[#673AB7] hover:bg-[#5e35a6] text-white text-[13px] font-medium rounded-lg transition disabled:opacity-70 flex items-center justify-center gap-2 shadow-sm"
                  >
                    {isMappingSaving ? <FiRefreshCw className="animate-spin" size={14}/> : <FiSave size={14}/>}
                    Save Mapping Rules
                  </button>
                </div>
              </div>

              {discoveredFields.length > 0 && (
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 ml-1.5">
                  <FiDatabase className="text-[#673AB7]" size={12} /> 
                  <span className="font-semibold">{discoveredFields.length} field name{discoveredFields.length === 1 ? '' : 's'}</span> detected from real traffic. Start typing in an 'Incoming Field' box to see suggestions.
                </p>
              )}
            </div>
          )}

          {/* Test Mapping Sandbox */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1.5">
              <FiPlay className="text-[#673AB7]" size={14}/>
              <h3 className="font-semibold cfg-heading text-[13px] text-gray-800 dark:text-white">Test Mapping Sandbox</h3>
            </div>
            <div className="p-4 bg-white dark:bg-gray-800">
              <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-3">
                Verify your rules before going live. Paste a sample JSON request below, and we'll show you exactly how it maps into CRM fields.
              </p>
              
              <div className="flex flex-col lg:flex-row gap-5">
                <div className="flex-1">
                  <textarea
                    value={testPayloadText}
                    onChange={(e) => setTestPayloadText(e.target.value)}
                    placeholder='{"name": "John", "mobile": "9876543210"}'
                    className={`${inputClass} font-mono min-h-[140px] resize-y mb-2`}
                  />
                  <button
                    onClick={testMapping}
                    disabled={isTesting}
                    className="w-full py-2 bg-gray-900 hover:bg-black dark:bg-gray-700 dark:hover:bg-gray-600 text-white text-[13px] font-medium rounded-lg transition shadow-sm flex justify-center items-center gap-1.5"
                  >
                    {isTesting ? <FiRefreshCw className="animate-spin" size={14}/> : <FiPlay size={14}/>}
                    Run Simulation
                  </button>
                  
                  {testError && (
                    <div className="mt-3 p-2.5 bg-red-50 text-red-600 rounded-lg border border-red-100 text-xs flex items-center gap-1.5">
                      <FiAlertCircle size={14}/> {testError}
                    </div>
                  )}
                </div>

                {/* Simulation Result Box */}
                {testResult && (
                  <div className="flex-1 bg-green-50/50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/50 rounded-xl p-4 animate-fade-in">
                    <h4 className="text-[13px] font-bold text-green-700 dark:text-green-400 mb-3 flex items-center gap-1.5 border-b border-green-200 dark:border-green-800 pb-1.5">
                      <FiCheckCircle size={16} /> Mapped Lead Data
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {RESULT_FIELDS.filter((f) => testResult[f]).map((f) => (
                        <div key={f} className="bg-white dark:bg-gray-800 rounded-lg px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 shadow-sm">
                          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">{f}</p>
                          <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 break-all">{String(testResult[f])}</p>
                        </div>
                      ))}
                    </div>

                    {testResult.customFields && Object.keys(testResult.customFields).length > 0 && (
                      <>
                        <h5 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 mt-3">Custom Data Included</h5>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {Object.entries(testResult.customFields).map(([k, v]) => (
                            <div key={k} className="bg-white dark:bg-gray-800 rounded-lg px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 shadow-sm border-l-2 border-l-[#673AB7]">
                              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">{k}</p>
                              <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 break-all">{String(v)}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    <details className="mt-3 group">
                      <summary className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 cursor-pointer flex items-center gap-1 hover:text-[#673AB7] transition select-none">
                        <FiCode size={12} /> View Raw Payload Logic
                      </summary>
                      <pre className="mt-2 text-[11px] font-mono bg-white dark:bg-gray-900 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto text-gray-600 dark:text-gray-300">
                        {JSON.stringify(testResult.usedPayload, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default KeyIntegration;